// frontend/packages/business-console/src/pages/Create.jsx
//
// "Create" — the proactive creation hub (mobile bottom-tab). One screen that
// answers "make a thing," then routes to the correct engine
// (docs/design/mobile-console-ux.md §5.5):
//
//   - Estimate / Invoice -> structured CrmQuote / CrmInvoice editors (D17).
//     These are NOT workflow forms; they open quotes/new + invoices/new,
//     which already carry an in-editor customer picker.
//   - Checklists / assessments / inspections -> the workflow-form filler
//     (forms/:slug/new, D19), listed from the tenant's published templates.
//
// The unification here is the entry point only — we do not merge data models.
// The mandatory step-0 customer picker (D22) is a later, backend-gated phase;
// for now estimate/invoice enforce a customer in their own editors.

import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { BusinessContext } from '../contexts/BusinessContext';
import { listWorkflowForms } from '../services/workflowFormService';

export default function Create() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { business } = useContext(BusinessContext);
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listWorkflowForms();
      setForms(res?.items || []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Couldn't load your forms."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Structured documents (D17) — two-up grid of primary tiles.
  const structured = [
    {
      key: 'estimate',
      title: 'Estimate',
      desc: 'Estimate a job',
      Icon: RequestQuoteOutlinedIcon,
      to: `${basePath}/quotes/new`,
    },
    {
      key: 'invoice',
      title: 'Invoice',
      desc: 'Bill a customer',
      Icon: ReceiptLongOutlinedIcon,
      to: `${basePath}/invoices/new`,
    },
  ];

  const rowMinHeight = theme.custom.mobile.actionRowHeight;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          Create
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Start an estimate, invoice, or field form.
        </Typography>
      </Box>

      {/* Structured documents */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1.5,
        }}
      >
        {structured.map(({ key, title, desc, Icon, to }) => (
          <Paper
            key={key}
            variant="outlined"
            onClick={() => navigate(to)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') navigate(to);
            }}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              cursor: 'pointer',
              minHeight: 96,
              transition: 'border-color 0.2s, box-shadow 0.2s',
              '&:active': { borderColor: 'primary.main' },
            }}
          >
            <Icon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{title}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {desc}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Workflow forms (checklists, assessments, inspections) */}
      <Box>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}
        >
          Checklists &amp; assessments
        </Typography>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            sx={{ mt: 1 }}
            onClose={() => setError(null)}
            action={
              <Typography
                component="button"
                onClick={load}
                sx={{
                  border: 0,
                  background: 'none',
                  color: 'inherit',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Retry
              </Typography>
            }
          >
            {error}
          </Alert>
        ) : forms.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', mt: 1 }}>
            <DescriptionOutlinedIcon
              sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No field forms published yet. Once a checklist or assessment is
              published, it&apos;ll appear here to fill on the go.
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}
          >
            {forms.map((f) => (
              <Paper
                key={f._id || f.slug}
                variant="outlined"
                onClick={() => navigate(`${basePath}/forms/${f.slug}/new`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    navigate(`${basePath}/forms/${f.slug}/new`);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  minHeight: rowMinHeight,
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                  '&:active': { borderColor: 'primary.main' },
                }}
              >
                <AssignmentOutlinedIcon sx={{ color: 'primary.main' }} />
                <Typography sx={{ fontWeight: 700, flex: 1 }}>
                  {f.name}
                </Typography>
                <ChevronRightIcon sx={{ color: 'text.disabled' }} />
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
